import * as yup from 'yup'
import dayjs from 'dayjs'
import {
  CourseLevel,
  // CourseVisibility,
  DiscountTypeOptions,
} from '../../core/common/data/json/courses'
import { getCategories } from '../../api/courses'
import { benefitsIcons } from '../../core/common/data/json/benefitsIcon'
import {
  normalizeLeadingInteger,
  normalizeMaterialJpl,
} from '../../utils/numberNormalizer'
import { setAdminFavoriteOverride } from '../../utils/favoriteStore'

// --- Step 1: Course Information ---
export const courseInformationFieldsConfig = {
  title: {
    label: 'Course Title',
    type: 'text',
    required: true,
    placeholder: 'Enter course title',
    schema: yup.string().required('Course Title is required'),
  },
  category_id: {
    label: 'Course Category',
    type: 'select',
    required: true,
    placeholder: 'Select category',
    options: [],
    isSearchableAndInfinite: true,
    queryKeyBase: 'categories',
    apiFn: (params: any) => getCategories({ ...params, type: 'course' }),
    schema: yup.string().required('Course Category is required'),
  },
  course_level: {
    label: 'Course Level',
    type: 'select',
    required: true,
    placeholder: 'Select level',
    options: CourseLevel,
    schema: yup.string().required('Course Level is required'),
  },
  max_student: {
    label: 'Max Number of Students',
    type: 'number',
    required: true,
    placeholder: 'e.g., 50',
    schema: yup
      .number()
      .required('Max Number of Students is required')
      .positive('Must be a positive number')
      .integer('Must be an integer')
      .typeError('Must be a number'),
  },
  // is_public: {
  //   label: 'Public / Private Course',
  //   type: 'select',
  //   required: false,
  //   placeholder: 'Select visibility',
  //   options: CourseVisibility,
  //   schema: yup
  //     .number()
  //     .oneOf([0, 1], 'Invalid visibility option')
  //     .required('Course Visibility is required'),
  // },
  short_description: {
    label: 'Short Description',
    type: 'text',
    required: true,
    placeholder: 'Briefly describe the course',
    schema: yup
      .string()
      .required('Short Description is required')
      .max(255, 'Short Description must not exceed 255 characters'),
  },
  is_favorite: {
    label: 'Tandai Kelas Ini Sebagai Terfavorit (Best Seller)',
    type: 'checkbox',
    required: false,
    placeholder: '',
    schema: yup.boolean().optional().default(false),
  },
  description: {
    label: 'Course Description',
    type: 'richtext',
    required: true,
    placeholder: 'Provide a detailed description of the course',
    schema: yup.string().required('Course Description is required'),
  },
  studied: {
    label: 'Apa yang akan dipelajari',
    type: 'array',
    required: true,
    placeholder: 'Apa yang akan dipelajari',
    schema: yup.array(
      yup.string().required('Apa yang akan dipelajari is required'),
    ),
  },
  thumbnail: {
    label: 'Course Thumbnail',
    type: 'file',
    required: true,
    placeholder: 'Upload image',
    schema: yup
      .mixed()
      .test('fileRequired', 'Course Thumbnail is required', (value: any) => {
        if (Array.isArray(value)) return value.length > 0
        return !!value
      })
      .test(
        'fileType',
        'Unsupported File Format (only JPG, JPEG, PNG)',
        (value: any) => {
          const file = value?.[0]
          if (!file && typeof file !== 'string') {
            return true
          }

          if (typeof file === 'string')
            return ['jpeg', 'png', 'jpg'].includes(value.split('.')[1])
          return ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)
        },
      )
      .test('fileSize', 'File too large (max 5MB)', (value: any) => {
        const file = value?.[0]
        if (!file && typeof file !== 'string') return true
        if (typeof file === 'string') return true

        return file.size <= 5 * 1024 * 1024
      }),
  },
  included_course_ids: {
    label: 'Included Courses (CBT/Other Courses)',
    type: 'multi-select',
    required: false,
    placeholder: 'Pilih course yang otomatis terdaftar',
    schema: yup.array().of(yup.number().required()).nullable(),
  },
}

// --- Step 2: Content ---
export const contentFieldsConfig = {
  topic: {
    label: 'Course Topics & Lessons',
    placeholder: 'Course Topics & Lessons',
    type: 'hidden',
    required: true,
    schema: yup
      .array(
        yup.object().shape({
          name: yup.string().required('Topic name is required'),
          topic_id: yup.number().nullable().optional(), // Backend ID for existing topics
          lesson: yup
            .array(
              yup.object().shape({
                name: yup.string().required('Lesson name is required'),
                video_link: yup
                  .string()
                  .url('Must be a valid URL')
                  .required('Video URL is required'),
                duration: yup
                  .string()
                  .required('Duration is required')
                  .matches(/^\d{1,2}:\d{2}:\d{2}$/, 'Duration must be in HH:MM:SS format'),
                description: yup.string(),
                is_premium: yup.boolean(),
                lesson_id: yup.number().nullable().optional(), // Backend ID for existing lessons
              }),
            )
            .min(1, 'At least one lesson is required for each topic')
            .required('Lessons are required'),
        }),
      )
      .min(1, 'At least one topic is required')
      .required('Course content is required'),
  },
}

// --- Step 2b: Question & Answer (Posttest) ---
const courseQuestionAnswersSchema = yup.object().shape({
  question: yup.string().required('Question is required'),
  discussion: yup.string().nullable(),
  normal_lab: yup.string().nullable(),
  answers: yup
    .array(
      yup.object().shape({
        choice: yup.string().required('Choice is required'),
        is_true: yup.boolean(),
      }),
    )
    .min(1, 'Minimal satu jawaban wajib diisi')
    .test(
      'one-correct-answer',
      'Setidaknya satu jawaban harus benar',
      (value) => {
        if (!value || value.length === 0) return true
        return value.some((answer) => answer.is_true)
      },
    ),
})

export const courseQuestionInformationFieldsConfig = {
  has_questions: {
    label: 'Ada Post-test (Quiz CBT)?',
    type: 'radio',
    required: true,
    options: [
      { label: 'Tidak', value: 'no' },
      { label: 'Ya', value: 'yes' },
    ],
    schema: yup.string().required('Pilih apakah ada Post-test'),
  },
  duration: {
    label: 'Durasi',
    type: 'time',
    required: true,
    placeholder: 'Atur durasi',
    schema: yup
      .string()
      .when('has_questions', {
        is: 'yes',
        then: (schema) =>
          schema.required('Durasi is required').typeError('Durasi tidak valid'),
        otherwise: (schema) => schema.nullable(),
      }),
  },
  min_passing_score: {
    label: 'Minimum Passing Score',
    type: 'number',
    required: true,
    placeholder: 'Atur Min Score',
    schema: yup
      .string()
      .when('has_questions', {
        is: 'yes',
        then: (schema) =>
          schema
            .required('Minimum Passing Score is required')
            .typeError('Minimum Passing Score tidak valid'),
        otherwise: (schema) => schema.nullable(),
      }),
  },
  max_attempt: {
    label: 'Jumlah maksimal pengerjaan Try Out',
    type: 'number',
    required: true,
    placeholder: 'Maksimal Pengerjaan',
    schema: yup
      .string()
      .when('has_questions', {
        is: 'yes',
        then: (schema) =>
          schema
            .required('Jumlah maksimal pengerjaan Try Out is required')
            .typeError('Jumlah maksimal pengerjaan Try Out tidak valid'),
        otherwise: (schema) => schema.nullable(),
      }),
  },
  questions: {
    label: 'Question & Answer',
    type: 'array',
    required: false,
    placeholder: 'Question & Answer',
    schema: yup
      .array(courseQuestionAnswersSchema)
      .when('has_questions', {
        is: 'yes',
        then: (schema) =>
          schema
            .min(1, 'Minimal satu pertanyaan wajib ditambahkan')
            .required('Question & Answer section is required.'),
        otherwise: (schema) => schema,
      }),
  },
}

// --- Step 3: Additional Information ---
export const additionalInformationFieldsConfig = {
  link_group: {
    label: 'Link Group',
    type: 'url',
    required: true,
    placeholder: 'e.g., https://t.me/yourgroup',
    schema: yup
      .string()
      .url('Must be a valid URL')
      .required('Link Group is required'),
  },
  link_ebook: {
    label: 'Link Ebook',
    type: 'url',
    required: true,
    placeholder: 'e.g., https://example.com/ebook.pdf',
    schema: yup
      .string()
      .url('Must be a valid URL')
      .required('Link Ebook is required'),
  },
  link_group_loker: {
    label: 'Link Group Lowongan Kerja',
    type: 'url',
    required: false,
    placeholder: 'e.g., https://t.me/lokergroup (Opsional)',
    schema: yup
      .string()
      .transform((value) => (value === '' ? null : value))
      .url('Must be a valid URL')
      .optional()
      .nullable(),
  },
  link_feedback: {
    label: 'Link Feedback Peserta',
    type: 'url',
    required: true,
    placeholder: 'e.g., https://forms.gle/feedback',
    schema: yup
      .string()
      .url('Must be a valid URL')
      .required('Link Feedback Peserta wajib diisi'),
  },
  slug: {
    label: 'Slugs / Keywords',
    type: 'text',
    required: true,
    placeholder: 'Slug',
    schema: yup.string().nullable(),
  },
  benefits: {
    label: 'Benefits',
    type: 'array',
    required: false,
    options: benefitsIcons,
    schema: yup
      .array(
        yup.object().shape({
          icon: yup.string().required('Icon is required'),
          name: yup.string().required('Benefit is required'),
        }),
      )
      .nullable()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          try {
            const parsed = JSON.parse(originalValue)
            return Array.isArray(parsed) ? parsed : null
          } catch {
            return null
          }
        }
        return value
      }),
  },
  certificate_event_dates: {
    label: 'Tanggal Pelaksanaan Sertifikat',
    type: 'textarea',
    required: false,
    placeholder: 'Contoh: Held on 30, 31 May and 1 June 2026',
    schema: yup.string().nullable().optional(),
  },
  certificate_jpl_total: {
    label: 'Total JPL',
    type: 'text',
    required: false,
    placeholder: 'Contoh: 12 JP',
    schema: yup.string().nullable(),
  },
  certificate_jpl_note: {
    label: 'Catatan JPL',
    type: 'text',
    required: false,
    placeholder: 'Contoh: *) 1 JPL: 45 minutes',
    schema: yup.string().nullable(),
  },
  certificate_materials: {
    label: 'Materi Sertifikat',
    type: 'certificate_materials',
    required: false,
    placeholder:
      'Format per baris: Materi | JPL | Narasumber\nContoh: Materi A | 2 JP | apt. Nama Narasumber',
    schema: yup.string().nullable(),
  },
  certificate_location: {
    label: 'Lokasi Sertifikat',
    type: 'text',
    required: false,
    placeholder: 'Contoh: Bandar Lampung',
    defaultValue: 'Bandar Lampung',
    schema: yup.string().nullable(),
  },
}

// --- Step 4: Pricing ---
export const pricingFieldsConfig = {
  price: {
    label: 'Course Price',
    type: 'number',
    required: true,
    placeholder: 'e.g., 250000 or 0 for free',
    schema: yup
      .number()
      .required('Course Price is required')
      .min(0, 'Price cannot be negative')
      .typeError('Must be a number'),
  },
  discount_type: {
    label: 'Discount Type',
    type: 'select',
    required: true,
    placeholder: 'Discount',
    options: DiscountTypeOptions,
    schema: yup.string().required('Discount type is required'),
  },
  discount: {
    label: 'Discount Value (Optional)',
    type: 'number',
    required: false,
    placeholder: 'e.g., 50 (for percentage) or 50000 (for amount)',
    schema: yup
      .number()
      .typeError('Masukan 0 jika tidak ingin ada diskon')
      .min(0, 'Nilai diskon minimal 0')
      .nullable()
      // .when('discount_type', {
      //   is: (val: string) => val !== 'NONE',
      //   then: (schema) => schema.required("Discount value is required when discount type is not 'None'").positive("Discount value must be positive").typeError("Must be a number"),
      //   otherwise: (schema) => schema.nullable(),
      // })
      // Add validation for percentage vs amount if needed, e.g., percentage <= 100
      .test(
        'discount-percentage-max',
        'Percentage cannot exceed 100%',
        function (value) {
          const { discount_type } = this.parent
          if (
            discount_type === 'PERCENTAGE' &&
            typeof value === 'number' &&
            value > 100
          ) {
            return this.createError({
              message: 'Percentage cannot exceed 100%',
            })
          }
          return true
        },
      ),
  },
  cross_sell: {
    label: 'List Product Cross Sell',
    type: 'array',
    placeholder: 'Cross Sell',
    required: false,
    options: [],
    schema: yup
      .array(
        yup.object().shape({
          cross_course_id: yup
            .number()
            .required('Cross-sell course ID is required'),
          note: yup.string().nullable(),
          price: yup
            .number()
            .required('Price is required')
            .typeError('Price must be a number'),
        }),
      )
      .nullable()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          try {
            const parsed = JSON.parse(originalValue)
            return Array.isArray(parsed) ? parsed : null
          } catch {
            return null
          }
        }
        return value
      }),
  },
}

export const courseBasicInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(courseInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const courseContentSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(contentFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const courseQuestionInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(courseQuestionInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const courseAdditionalInformationSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(additionalInformationFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const coursePricingSchema = yup
  .object()
  .shape(
    Object.fromEntries(
      Object.entries(pricingFieldsConfig).map(([key, val]) => [
        key,
        val.schema,
      ]),
    ),
  )

export const fullCourseCreateSchema = yup.object().shape({
  ...courseBasicInformationSchema.fields,
  ...courseContentSchema.fields,
  ...courseQuestionInformationSchema.fields,
  ...courseAdditionalInformationSchema.fields,
  ...coursePricingSchema.fields,
})

export type CourseBasicInformationFormValues = yup.InferType<
  typeof courseBasicInformationSchema
>
export type CourseContentFormValues = yup.InferType<typeof courseContentSchema>
export type CourseQuestionInformationFormValues = yup.InferType<
  typeof courseQuestionInformationSchema
>
export type CourseAdditionalInformationFormValues = yup.InferType<
  typeof courseAdditionalInformationSchema
>
export type CoursePricingFormValues = yup.InferType<typeof coursePricingSchema>

export type CourseCreateFormValues = CourseBasicInformationFormValues &
  CourseContentFormValues &
  CourseQuestionInformationFormValues &
  CourseAdditionalInformationFormValues &
  CoursePricingFormValues

export function getInitialValuesFromConfig(
  config: Record<string, any>,
): Record<string, any> {
  return Object.keys(config).reduce((acc, key) => {
    const field = config[key]
    if (field.defaultValue !== undefined) {
      acc[key] = field.defaultValue
    } else if (field.type === 'checkbox') {
      acc[key] = false
    } else if (field.type === 'file') {
      acc[key] = null
    } else if (field.type === 'array' || field.type === 'chips') {
      acc[key] = []
    } else if (field.type === 'multi-select') {
      acc[key] = []
    } else if (field.type === 'number') {
      acc[key] = ''
    } else if (field.type === 'richtext') {
      acc[key] = ''
    } else if (field.type === 'radio') {
      acc[key] = field.options?.[0]?.value || ''
    } else {
      acc[key] = null
    }
    return acc
  }, {} as Record<string, any>)
}

export function parseCertificateMaterials(value: any) {
  if (Array.isArray(value)) return value
  if (!value || typeof value !== 'string') return []

  const trimmedValue = value.trim()
  if (!trimmedValue) return []

  if (trimmedValue.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmedValue)
      if (Array.isArray(parsed)) return parsed
    } catch {
      // Fall back to the pipe-separated format below.
    }
  }

  return trimmedValue
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [topic = '', jpl = '', speaker = ''] = line
        .split('|')
        .map((part) => part.trim())

      return { topic, jpl, speaker }
    })
    .filter((item) => item.topic || item.jpl || item.speaker)
}

export function formatCertificateMaterials(value: any) {
  let materials = value

  if (typeof value === 'string') {
    const trimmedValue = value.trim()

    if (trimmedValue.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmedValue)
        if (Array.isArray(parsed)) {
          materials = parsed
        }
      } catch {
        return value || ''
      }
    }
  }

  if (!Array.isArray(materials)) return materials || ''

  return materials
    .map((item) =>
      [item?.topic || '', item?.jpl || '', item?.speaker || ''].join(' | '),
    )
    .join('\n')
}

// Initial values for each step
export const initialValuesStep1 = getInitialValuesFromConfig(
  courseInformationFieldsConfig,
)
export const initialValuesStep2 = getInitialValuesFromConfig(
  contentFieldsConfig,
)
export const initialValuesStep2b = getInitialValuesFromConfig(
  courseQuestionInformationFieldsConfig,
)
export const initialValuesStep3 = getInitialValuesFromConfig(
  additionalInformationFieldsConfig,
)
export const initialValuesStep4 = getInitialValuesFromConfig(
  pricingFieldsConfig,
)

export const initialFullFormValues: CourseCreateFormValues = {
  ...initialValuesStep1,
  ...initialValuesStep2,
  ...initialValuesStep2b,
  ...initialValuesStep3,
  ...initialValuesStep4,
}

// --- Function to transform Formik values into the desired API payload structure ---
export function transformCoursePayload(
  values: any,
  originalValues?: any,
): FormData {
  const formData = new FormData()

  const safeStr = (val: any): string => {
    if (val === null || val === undefined) return ''
    if (typeof val === 'object' && !Array.isArray(val)) return ''
    return String(val)
  }

  // Step 1 Fields
  formData.append('title', safeStr(values.title))
  formData.append('category_id', safeStr(values.category_id))
  formData.append('course_level', safeStr(values.course_level))
  formData.append('max_student', normalizeLeadingInteger(values.max_student))
  if (values.id || values.course_id) {
    formData.append('_method', 'PUT')
  }
  formData.append('short_description', safeStr(values.short_description))
  const isFav =
    values.is_favorite === true ||
    values.is_favorite === 1 ||
    values.is_favorite === '1' ||
    values.is_favorite === 'true'

  if (values.id || values.course_id) {
    setAdminFavoriteOverride(values.id || values.course_id, isFav)
  }

  const favValStr = isFav ? '1' : '0'
  formData.append('is_favorite', favValStr)
  formData.append('is_favourite', favValStr)
  formData.append('is_bestseller', favValStr)
  formData.append('is_best_seller', favValStr)
  formData.append('is_featured', favValStr)
  formData.append('is_top', favValStr)
  formData.append('is_popular', favValStr)
  formData.append('favorite', favValStr)
  formData.append('favourite', favValStr)
  formData.append('bestseller', favValStr)
  formData.append('badge', isFav ? 'bestseller' : '')
  formData.append('description', safeStr(values.description))
  formData.append(
    'included_course_ids',
    JSON.stringify(values.included_course_ids || []),
  )

  formData.append('studied', JSON.stringify(values.studied || []))

  // Handle thumbnail: user can only upload new file or keep existing
  if (values?.thumbnail && typeof values.thumbnail !== 'string') {
    formData.append('thumbnail', values.thumbnail[0] || values.thumbnail)
  }

  // Step 2 Fields (Topic) - Transform back to backend format with proper IDs
  const transformedTopics = (values.topic || []).map((topic: any) => ({
    ...topic,
    id: topic.topic_id || topic.id,
    lesson: (topic.lesson || topic.lessons || []).map((lesson: any) => ({
      ...lesson,
      id: lesson.lesson_id || lesson.id,
    })),
  }))
  formData.append('topic', JSON.stringify(transformedTopics))

  // Step 3 Fields
  formData.append('link_ebook', safeStr(values.link_ebook))
  formData.append('link_group', safeStr(values.link_group))
  formData.append('link_group_loker', safeStr(values.link_group_loker))
  formData.append('link_feedback', safeStr(values.link_feedback))
  formData.append('slug', safeStr(values.slug))
  formData.append('cross_sell', JSON.stringify(values.cross_sell || []))
  formData.append('benefits', JSON.stringify(values.benefits || []))

  if (values.certificate_event_dates !== undefined) {
    formData.append('certificate_event_dates', safeStr(values.certificate_event_dates))
  }
  formData.append(
    'certificate_jpl_total',
    normalizeLeadingInteger(values.certificate_jpl_total || ''),
  )
  if (values.certificate_jpl_note !== undefined) {
    formData.append('certificate_jpl_note', safeStr(values.certificate_jpl_note))
  }
  if (values.certificate_materials !== undefined) {
    const parsedMaterials = parseCertificateMaterials(values.certificate_materials)
    const sourceMaterials = parsedMaterials.length
      ? parsedMaterials
      : (values.studied || []).map((topic: string) => ({
          topic,
          jpl: '',
          speaker: '',
        }))
    const certificateMaterials = sourceMaterials.map((item: any) => ({
      topic: item.topic || '',
      jpl: normalizeMaterialJpl(item.jpl || ''),
      speaker: item.speaker || '',
    }))

    formData.append(
      'certificate_materials',
      JSON.stringify(
        certificateMaterials.filter((item: any) => item.topic || item.jpl || item.speaker),
      ),
    )
  }
  if (values.certificate_location !== undefined) {
    formData.append('certificate_location', safeStr(values.certificate_location))
  }

  // Step 2b Questions Fields
  const hasQuestions = values.has_questions === 'yes'
  if (hasQuestions) {
    const hour = dayjs(values.duration as any).hour() || 0
    const minute = dayjs(values.duration as any).minute() || 0
    const totalMs: number = (hour * 60 + minute) * 60 * 1000

    formData.append(
      'min_passing_score',
      normalizeLeadingInteger(values.min_passing_score),
    )
    formData.append('max_attempt', normalizeLeadingInteger(values.max_attempt))
    formData.append('duration', totalMs.toString())
  } else {
    formData.append('min_passing_score', '0')
    formData.append('max_attempt', '0')
    formData.append('duration', '0')
    formData.append('questions', JSON.stringify([]))
  }

  if (hasQuestions && Array.isArray(values.questions)) {
    const questionsJSON = values.questions.map((q: any) => ({
      ...(q.id ? { id: q.id } : {}),
      question: safeStr(q.question),
      discussion: safeStr(q.discussion).trim() || '-',
      normal_lab: safeStr(q.normal_lab),
      answers: (q.answers || []).map((ans: any) => ({
        ...(ans.id ? { id: ans.id } : {}),
        choice: safeStr(ans.choice),
        is_true: ans.is_true ? 'true' : 'false',
      })),
    }))
    formData.append('questions', JSON.stringify(questionsJSON))

    values.questions.forEach((q: any, i: number) => {
      if (q.image && q.image instanceof File) {
        formData.append(`questions[${i}][image]`, q.image)
      }
    })
  }

  // Step 4 Fields
  formData.append('price', safeStr(values.price || 0))
  formData.append('discount_type', safeStr(values.discount_type || 'NONE'))
  if (
    values.discount !== null &&
    values.discount !== undefined &&
    values.discount !== ''
  ) {
    formData.append('discount', safeStr(values.discount))
  }

  formData.append('type', 'Course')

  return formData
}
